import { memo, useState, useCallback, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { Wand2, X, Loader2, CheckCircle, AlertCircle, Play, Download, Eye, Plus, ImagePlus, RefreshCw } from 'lucide-react';
import { downloadFile } from '../../utils/downloadFile';
import { blobToFile } from '../../utils/mediaUtils';
import { useWorkflowStore } from '../../store/workflowStore';
import { useNodeExecution } from '../../hooks/useNodeExecution';
import { useWorkflowTrigger } from '../../hooks/useWorkflowTrigger';
import NodeError from './NodeError';
import NodeResetButton from './NodeResetButton';
import {
  ReconfigureButton, ReconfigureNotice, useReconfigure,
} from './ReconfigureControls';
import { useMediaPreview } from '../../hooks/useMediaPreview';
import { setSourcePickerHandler } from '../../utils/sourcePickerBridge';
import SendToTimelineButton from '../SendToTimelineButton';
import DirectorsTrigger from '../studio/DirectorsTrigger';
import PromptField from '../studio/PromptField';
import NodeRequirements from './NodeRequirements';
import { missingOf, requirementsTitle, textNeed } from '../../utils/nodeRequirements';

const ASPECT_RATIOS = [
  { value: '', label: 'Auto' },
  { value: '1:1', label: '1:1' },
  { value: '3:4', label: '3:4' },
  { value: '4:3', label: '4:3' },
  { value: '9:16', label: '9:16' },
  { value: '16:9', label: '16:9' },
];

const OUTPUT_FORMATS = [
  { value: 'jpg', label: 'JPG' },
  { value: 'png', label: 'PNG' },
  { value: 'webp', label: 'WebP' },
];

const QUICK_PROMPTS = [
  { label: 'Ghibli', prompt: 'Transform this image into a Studio Ghibli anime style illustration' },
  { label: 'Invierno', prompt: 'Make this scene look like it is set during a snowy winter night with warm light glowing from the windows' },
  { label: 'Selfie', prompt: 'Have the people smile and take a selfie from a phone' },
  { label: 'Poster', prompt: 'A vintage travel poster in the style of 1960s airline advertisements' },
];

const INPUT_HANDLES = [
  { id: 'input1', top: '25%', label: '1' },
  { id: 'input2', top: '50%', label: '2' },
  { id: 'input3', top: '75%', label: '3' },
];

const NanoBananaNode = memo(({ id, data, selected }) => {
  const [chronometer, setChronometer] = useState(0);
  const [prompt, setPrompt] = useState(data.prompt || '');
  const [images, setImages] = useState(data.images || []);
  const [aspectRatio, setAspectRatio] = useState(data.aspectRatio || '');
  const [outputFormat, setOutputFormat] = useState(data.outputFormat || 'jpg');
  const [lastPrompt, setLastPrompt] = useState(data.lastPrompt || '');

  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const deleteNode = useWorkflowStore((state) => state.deleteNode);
  const getInputForNode = useWorkflowStore((state) => state.getInputForNode);
  const addAsset = useWorkflowStore((state) => state.addAsset);
  const edges = useWorkflowStore((state) => state.edges);
  const nodes = useWorkflowStore((state) => state.nodes);
  const openSourcePicker = useWorkflowStore((state) => state.openSourcePicker);

  const { processNode, status: execStatus, error: execError, reset: resetExec, insufficientCredits } = useNodeExecution();
  const { openImagePreview } = useMediaPreview();

  const isProcessing = execStatus === 'uploading' || execStatus === 'queued' || execStatus === 'processing';

  // Get all connected inputs (up to 3 handles)
  const connectedInputs = INPUT_HANDLES.map(h => {
    const edge = edges.find(e => e.target === id && e.targetHandle === h.id);
    if (!edge) return null;
    const sourceNode = nodes.find(n => n.id === edge.source);
    return sourceNode?.data?.hasOutput ? sourceNode.data : null;
  });
  const connectedCount = connectedInputs.filter(Boolean).length;

  useEffect(() => {
    updateNodeData(id, { prompt, images, aspectRatio, outputFormat });
  }, [prompt, aspectRatio, outputFormat]);

  const handleAddImage = useCallback(() => {
    if (images.length >= 3) return;
    setSourcePickerHandler((result) => {
      if (result.file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setImages(prev => {
            const updated = [...prev, { file: result.file, preview: e.target.result, name: result.file.name }];
            updateNodeData(id, { images: updated });
            return updated;
          });
        };
        reader.readAsDataURL(result.file);
      } else if (result.url) {
        setImages(prev => {
          const updated = [...prev, { file: null, preview: result.url, name: result.fileName, fromGallery: true }];
          updateNodeData(id, { images: updated });
          return updated;
        });
      }
    });
    openSourcePicker(id, ['image'], 'image/jpeg,image/jpg,image/png,image/webp');
  }, [id, images.length, openSourcePicker, updateNodeData]);

  const removeImage = useCallback((index) => {
    setImages(prev => {
      const updated = prev.filter((_, i) => i !== index);
      updateNodeData(id, { images: updated });
      return updated;
    });
  }, [id, updateNodeData]);

  useEffect(() => {
    let interval;
    if (isProcessing) {
      setChronometer(0);
      interval = setInterval(() => setChronometer((prev) => prev + 1), 1000);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [isProcessing]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const fetchFileFromUrl = async (url, filename) => blobToFile(url, filename, 'image/jpeg');

  // Regenerar descarta el resultado; volver a configurar lo conserva.
  const { reconfigure, setReconfigure, showForm } = useReconfigure(data);

  const handleProcess = useCallback(async () => {
    if (!prompt.trim()) return false;
    resetExec();

    const allImageFiles = [];
    const directImageUrls = [];

    // Collect from connected handles
    for (const input of connectedInputs) {
      if (!input) continue;
      let file = input.file;
      if (!file) {
        const fileUrl = input.outputUrl || input.preview;
        if (fileUrl?.startsWith('http')) {
          directImageUrls.push(fileUrl);
          continue;
        } else if (fileUrl) {
          file = await fetchFileFromUrl(fileUrl, input.fileName || 'connected.jpg');
        }
      }
      if (file) allImageFiles.push(file);
    }

    // Collect manually added images
    for (const img of images) {
      let file = img.file;
      if (!file && img.preview) {
        if (img.preview.startsWith('http')) {
          directImageUrls.push(img.preview);
        } else {
          file = await fetchFileFromUrl(img.preview, img.name || 'image.jpg');
        }
      }
      if (file) allImageFiles.push(file);
    }

    const files = allImageFiles.map((file, i) => ({ key: `image_input_${i}`, file }));
    const params = { prompt: prompt.trim(), image_count: allImageFiles.length + directImageUrls.length };
    if (aspectRatio) params.aspect_ratio = aspectRatio;
    if (outputFormat) params.output_format = outputFormat;
    directImageUrls.forEach((url, i) => {
      params[`image_input_${allImageFiles.length + i}`] = url;
    });

    const usedPrompt = prompt.trim();

    const result = await processNode({
      resultType: 'nano_banana',
      files,
      params,
    });

    if (result?.outputUrl) {
      setLastPrompt(usedPrompt);
      setReconfigure(false);
      updateNodeData(id, {
        hasOutput: true,
        outputUrl: result.outputUrl,
        assetId: result.assetId ?? null,
        lastPrompt: usedPrompt,
        processedAt: new Date().toISOString(),
      });
      addAsset({
        id: `asset_${Date.now()}`,
        type: 'image',
        tool: 'nanoBanana',
        filename: `nanobanana_${Date.now()}.${outputFormat || 'jpg'}`,
        url: result.outputUrl,
        prompt: usedPrompt,
        createdAt: new Date().toISOString(),
      });
      return true;
    }
    return false;
  }, [id, prompt, images, connectedInputs, aspectRatio, outputFormat, updateNodeData, addAsset, processNode, resetExec]);

  const handleRegenerate = useCallback(() => {
    updateNodeData(id, { hasOutput: false, outputUrl: null });
    resetExec();
  }, [id, updateNodeData, resetExec]);

  // "Volver a configurar": vuelve al formulario con prompt, referencias y
  // parámetros tal como quedaron, conservando el resultado actual.

  const { isInQueue, isMyTurn } = useWorkflowTrigger(id, handleProcess);

  const totalImages = images.length + connectedCount;
  const canProcess = prompt.trim().length > 0;

  // NR-1 · qué le falta al nodo para generar (se muestra debajo del botón)
  const runMissing = missingOf([textNeed(prompt)]);

  return (
    <div className={`node-container nanoBanana-node ${selected ? 'selected' : ''} ${isInQueue ? 'workflow-queued' : ''} ${isMyTurn ? 'workflow-active' : ''}`}>
      <NodeResetButton nodeId={id} hasOutput={!!data.hasOutput} />

      {/* 3 Input Handles */}
      {INPUT_HANDLES.map((h) => (
        <Handle
          key={h.id}
          type="target"
          position={Position.Left}
          id={h.id}
          className="node-handle input-handle nano-input-handle"
          style={{ top: h.top }}
        />
      ))}

      {/* Header */}
      <div className="node-header nanoBanana-header">
        <div className="node-header-left">
          <Wand2 size={16} />
          <span>Nano Banana</span>
        </div>
        <button className="node-delete-btn" onClick={() => deleteNode(id)} data-testid="delete-nano-banana">
          <X size={14} />
        </button>
      </div>

      {/* Content */}
      <div className="node-content nano-content">
        {isProcessing ? (
          <div className="node-processing">
            <div className="node-chronometer">{formatTime(chronometer)}</div>
            <div className="node-processing-info">
              <Loader2 size={16} className="animate-spin" />
              <span>
                {execStatus === 'uploading' ? 'Subiendo archivos...' :
                 execStatus === 'queued' ? 'En cola...' :
                 'Generando imagen...'}
              </span>
            </div>
          </div>
        ) : !showForm ? (
          /* ===== RESULT VIEW: Image + Prompt editable + Regenerate ===== */
          <div className="node-result nano-result-view">
            <img
              src={data.outputUrl}
              alt="Nano Banana"
              className="node-result-image clickable"
              onClick={() => openImagePreview(data.outputUrl, 'Nano Banana')}
            />
            <div className="node-result-actions">
              <button className="node-mini-preview-btn" onClick={() => openImagePreview(data.outputUrl, 'Nano Banana')} data-testid="nano-preview-btn">
                <Eye size={12} /> Ver
              </button>
              <button className="node-mini-download-btn" onClick={(e) => { e.stopPropagation(); downloadFile(data.outputUrl, `nanobanana_${Date.now()}.${outputFormat || 'jpg'}`); }} data-testid="nano-download-btn">
                <Download size={12} /> Descargar
              </button>
              <SendToTimelineButton assetId={data.assetId} nodeId={id} url={data.outputUrl} kind="image" label="nanobanana" testIdPrefix="nanobanana-timeline" />
            </div>

            {/* Editable prompt for regeneration */}
            <div className="nano-regen-section">
              <span className="nano-section-label">Prompt usado:</span>
              <PromptField
                value={prompt}
                onChange={setPrompt}
                nodeId={id}
                nodeType="nanoBanana"
                label="Prompt de regeneración"
                className="nano-prompt-textarea nano-regen-textarea"
                rows={2}
                testId="nano-regen-prompt"
              />
              <button
                className="nano-regen-btn"
                onClick={handleRegenerate}
                data-testid="nano-regen-btn"
              >
                <RefreshCw size={12} />
                Regenerar
              </button>
              <ReconfigureButton
                onClick={() => setReconfigure(true)}
                testId="nano-reconfigure-btn"
              />
            </div>
          </div>
        ) : (
          <>
            {data.outputUrl && reconfigure && (
              <ReconfigureNotice
                previewUrl={data.outputUrl}
                onCancel={() => setReconfigure(false)}
                testId="nano-reconfigure-notice"
              />
            )}

            {/* Connected inputs indicator */}
            {connectedCount > 0 && (
              <div className="node-input-status connected">
                <CheckCircle size={12} />
                <span>{connectedCount} imagen{connectedCount > 1 ? 'es' : ''} conectada{connectedCount > 1 ? 's' : ''}</span>
              </div>
            )}

            {/* Manual image inputs */}
            <div className="nano-images-section">
              <span className="nano-section-label">
                <ImagePlus size={12} />
                Imágenes ({totalImages}/3) — opcional
              </span>
              <div className="nano-images-row">
                {images.map((img, index) => (
                  <div key={img.name || `img-${index}`} className="nano-img-thumb">
                    <img src={img.preview} alt={`Ref ${index + 1}`} />
                    <button className="nano-clear-btn" onClick={() => removeImage(index)} data-testid={`nano-remove-img-${index}`}>
                      <X size={8} />
                    </button>
                  </div>
                ))}
                {images.length < 3 && (
                  <button className="nano-add-img-btn" onClick={handleAddImage} data-testid="nano-add-image">
                    <Plus size={14} />
                  </button>
                )}
              </div>
            </div>

            {/* Prompt */}
            <div className="nano-prompt-section">
              <div className="nano-prompt-header">
                <span className="nano-section-label">Prompt *</span>
                <DirectorsTrigger nodeId={id} nodeType="nanoBanana" currentPrompt={prompt} onInsert={setPrompt} />
              </div>
              <PromptField
                value={prompt}
                onChange={setPrompt}
                nodeId={id}
                nodeType="nanoBanana"
                placeholder="Describe lo que quieres generar o editar..."
                className="nano-prompt-textarea"
                rows={3}
                testId="nano-prompt"
              />
              <div className="nano-quick-row">
                {QUICK_PROMPTS.map((qp) => (
                  <button
                    key={qp.label}
                    className={`nano-quick-btn ${prompt === qp.prompt ? 'active' : ''}`}
                    onClick={() => setPrompt(qp.prompt)}
                    data-testid={`nano-quick-${qp.label.toLowerCase()}`}
                  >
                    {qp.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Options row */}
            <div className="nano-options-row">
              <div className="nano-option">
                <span className="nano-option-label">Ratio</span>
                <select value={aspectRatio} onChange={(e) => setAspectRatio(e.target.value)} className="nano-select" data-testid="nano-aspect-ratio">
                  {ASPECT_RATIOS.map((ar) => (
                    <option key={ar.value} value={ar.value}>{ar.label}</option>
                  ))}
                </select>
              </div>
              <div className="nano-option">
                <span className="nano-option-label">Formato</span>
                <select value={outputFormat} onChange={(e) => setOutputFormat(e.target.value)} className="nano-select" data-testid="nano-output-format">
                  {OUTPUT_FORMATS.map((of_) => (
                    <option key={of_.value} value={of_.value}>{of_.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Process button */}
            <button
              className="node-process-btn nano-process-btn"
              onClick={handleProcess}
              disabled={!canProcess}
              title={requirementsTitle(runMissing)}
              data-testid="nano-process-btn"
            >
              <Play size={14} />
              {data.outputUrl && reconfigure
                ? 'Regenerar con cambios'
                : (totalImages > 0 ? 'Generar / Editar' : 'Generar')}
            </button>
            <NodeRequirements missing={runMissing} />
          </>
        )}

        <NodeError error={execError} insufficientCredits={insufficientCredits} />
      </div>

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="node-handle output-handle"
        isConnectable={!!data.hasOutput}
      />
    </div>
  );
});

NanoBananaNode.displayName = 'NanoBananaNode';

export default NanoBananaNode;
